import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { useAppSettings } from '@/context/app-settings';
import { LogoSettings } from './home/LogoSettings';
import { ExhibitAccessSettings } from './home/ExhibitAccessSettings';
import { WelcomeMessageEditor } from './home/WelcomeMessageEditor';
import { HomeElementsOrderManager } from './home/HomeElementsOrderManager';
import { ButtonLabelsEditor } from './home/ButtonLabelsEditor';
import { HomeImageSettings } from './home/HomeImageSettings';
import { BackgroundColorPicker } from './BackgroundColorPicker';

export function HomeSettings() {
  const { settings, saving, saveSettings, reloadSettings } = useAppSettings();
  
  if (!settings) {
    return <div>Loading...</div>;
  }

  const homeSettings = settings.home_settings_v2;

  const updateHomeSetting = async (key: string, value: any) => {
    await saveSettings({
      home_settings_v2: {
        ...homeSettings,
        [key]: value
      }
    });
  };

  const updateButtonSetting = async (buttonKey: string, value: boolean) => {
    const newButtonSettings = {
      ...homeSettings.exhibitAccessButtons,
      [buttonKey]: value
    };
    await updateHomeSetting('exhibitAccessButtons', newButtonSettings);
  };

  const updateButtonLabels = async (labels: Record<string, Record<string, string>>) => {
    await saveSettings({ button_labels: labels });
  };

  return (
    <div className="space-y-8">
      <div className="border-b border-border/30 pb-4">
        <h3 className="text-lg font-semibold text-foreground">Home Page Configuration</h3>
        <p className="text-sm text-muted-foreground mt-2">
          Control which elements are displayed on the home page
        </p>
      </div>

      <Card className="border border-border/60 bg-card/50">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <div className="w-2 h-2 bg-primary rounded-full"></div>
            Main Elements
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Show or hide the main sections of the home page
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 pt-2">
          <LogoSettings
            showLogo={homeSettings.showLogo}
            logoUrl={homeSettings.logoUrl}
            saving={saving}
            onUpdateSetting={updateHomeSetting}
          />

          <WelcomeMessageEditor
            showWelcomeMessage={homeSettings.showWelcomeMessage}
            useCustomWelcomeMessage={homeSettings.useCustomWelcomeMessage}
            welcomeMessageCustom={homeSettings.welcomeMessageCustom}
            saving={saving}
            onUpdateSetting={updateHomeSetting}
          />

          <HomeImageSettings
            showHomeImage={homeSettings.showHomeImage}
            homeImageSettings={homeSettings.homeImageSettings}
            saving={saving}
            onUpdateSetting={updateHomeSetting}
          />

          <HomeElementsOrderManager
            elementOrder={homeSettings.elementOrder}
            showLogo={homeSettings.showLogo}
            showWelcomeMessage={homeSettings.showWelcomeMessage}
            showExhibitAccess={homeSettings.showExhibitAccess}
            showVisitorInfo={homeSettings.showVisitorInfo}
            showHomeImage={homeSettings.showHomeImage}
            saving={saving}
            onUpdateOrder={(order) => updateHomeSetting('elementOrder', order)}
            onUpdateSetting={updateHomeSetting}
          />
        </CardContent>
      </Card>

      <Card className="border border-border/60 bg-card/50">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <div className="w-2 h-2 bg-primary rounded-full"></div>
            Exhibit Access Options
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Configure which access methods are available to visitors
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 pt-2">
          <ExhibitAccessSettings
            showExhibitAccess={homeSettings.showExhibitAccess}
            exhibitAccessButtons={homeSettings.exhibitAccessButtons}
            saving={saving}
            onUpdateSetting={updateHomeSetting}
            onUpdateButtonSetting={updateButtonSetting}
          />

          <div className="space-y-4 pt-6 border-t border-border/50">
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border/50">
              <div className="space-y-1">
                <label className="text-sm font-medium text-foreground">Background Shapes</label>
                <p className="text-sm text-muted-foreground">
                  Show decorative background shapes for visual appeal
                </p>
              </div>
              <Switch
                checked={homeSettings.showBackgroundShapes}
                onCheckedChange={(value) => updateHomeSetting('showBackgroundShapes', value)}
                disabled={saving}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-border/60 bg-card/50">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <div className="w-2 h-2 bg-primary rounded-full"></div>
            Background Color
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Customize the background appearance
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-2">
          <BackgroundColorPicker />
        </CardContent>
      </Card>

      <Card className="border border-border/60 bg-card/50">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <div className="w-2 h-2 bg-primary rounded-full"></div>
            Button Labels
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Customize button text throughout the application
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-2">
          <ButtonLabelsEditor
            buttonLabels={settings.button_labels || {}}
            saving={saving}
            onUpdateLabels={updateButtonLabels}
          />
        </CardContent>
      </Card>
    </div>
  );
}